/**
 * 环境
 */
import { getApp } from '@mx/util/get-app';
import { getSystem } from '@mx/util/get-system';
import { getAllCookie } from '@mx/util/get-all-cookie';
import { getAllQueryString } from '@mx/util/get-all-query-string';
import { bridge } from './bridge';

export enum Runtime {
  Prod,
  Staging,
  Test,
  Dev,
  Local,
  Proxy
}

class Env {
  system: string;
  isIOS: boolean;
  isAndroid: boolean;

  appName: string;
  isInConch: boolean;
  isInWaimai: boolean;
  isInMeituan: boolean;
  isInDianping: boolean;
  isNative: boolean = /knb/i.test(navigator.userAgent.toLowerCase());
  isHarmony: boolean = /openharmony/i.test(navigator.userAgent.toLowerCase());
  utmContent: string;
  utmSource: string;

  runtime: Runtime;

  // common params
  entry: string;
  scene: string;
  merchantNo: string;
  extDimStatEntry: string;

  commonParams: ICommonParams;
  memberId: string = '';

  // 生物识别信息
  biometricsInfoParams: IBiometricsInfoParams;

  constructor() {
    const cookies = getAllCookie();
    const urlParams = getAllQueryString();

    const getParam = (...names: string[]) => {
      for (let i = 0, len = names.length; i < len; i++) {
        const name = names[i];
        const param = cookies[name] || urlParams[name];
        if (param) {
          return param;
        }
      }
      return '';
    };

    if (/\.fe\.dev\.(sankuai|meituan|dianping)\.com/.test(location.host)) {
      this.runtime = Runtime.Proxy;
    } else if (/\.dev\.sankuai\.com/.test(location.host)) {
      this.runtime = Runtime.Dev;
    } else if (/\.test\.sankuai\.com/.test(location.host)) {
      this.runtime = Runtime.Test;
    } else if (/\.st\.(sankuai|meituan)\.com/.test(location.host)) {
      this.runtime = Runtime.Staging;
    } else if (/\.meituan\.com/.test(location.host)) {
      this.runtime = Runtime.Prod;
    } else {
      this.runtime = Runtime.Local;
    }

    // url params
    this.entry = urlParams.entry;
    this.scene = urlParams.scene;

    // 绑卡接入参数
    this.merchantNo = '10012';
    this.extDimStatEntry = 'no_pass_pay';

    // app
    this.system = getSystem();
    this.isIOS = this.system === 'ios';
    this.isAndroid = this.system === 'android';
    this.appName = getApp(urlParams.utm_campaign, navigator.userAgent);
    this.isInConch = this.appName === 'conch';
    this.isInWaimai = this.appName === 'waimai';
    this.isInMeituan = this.appName === 'group';
    this.isInDianping = this.appName === 'dianping-nova';
    this.utmContent = getParam('utm_content');
    this.utmSource = urlParams.utm_source || '';

    this.commonParams = {
      nb_uuid: getParam('iuuid', 'uuid', 'dpid'),
      nb_ci: getParam('nb_ci') || '1',
      nb_app: this.appName,
      nb_appversion: '',
      nb_deviceid: '',
      nb_device_model: '',
      nb_channel: 'touch',
      nb_fingerprint: '',
      nb_version: '',
      nb_platform: this.isHarmony ? 'harmony' : urlParams.utm_medium || (this.isIOS ? 'iphone' : 'android'),
      nb_adid: '',
      nb_location: '',
      userid: getParam('u', 'userid', 'userId'),
    };

    this.biometricsInfoParams = {
      printType: '',
      finger_data: '',
      face_data: '',
      has_faceid: 0,
      has_touchid: 0,
      support_faceid: 0,
    };
  }

  async initEnv() {
    if (bridge.isNative()) {
      await Promise.all([
        this.getAppInfo(), this.getDeviceInfo(), this.getLocation(), this.getBiometricsInfo(), this.getUserInfo()
      ]).then(() => {
        this.reportInitEnv();
      }).catch(() => {
        this.reportInitEnv();
      });
    }
  }

  getAppInfoFromHarmony(ua: string) {
    const uaPairs = ua.replace(/\([^)]+\)/g, '').split(/\s+/);
    const uaInfo: any = {};
    let lastItemKey = '';
    for (let i = 0; i < uaPairs.length; i += 1) {
      const item = uaPairs[i];
      const split = item.lastIndexOf('/');
      if (split < 0) {
        lastItemKey = item;
      } else {
        let itemKey = item.slice(0, split);
        if (lastItemKey) {
          itemKey = `${lastItemKey} ${itemKey}`;
          lastItemKey = '';
        }
        uaInfo[itemKey] = item.slice(split - item.length + 1);
      }
    }
    let appId;
    let appVersion;
    let hasAppId = false;
    Object.keys(uaInfo).forEach((key) => {
      if (hasAppId) { return; }
      if (key.indexOf('AppId/') > -1) {
        appVersion = uaInfo[key];
        appId = key.match(/\/(.*)/)?.[1] ?? '';
        if (appId) {
          hasAppId = true;
        }
      }
    });
    return { appId: appId || '', version: appVersion || '', appVersion: appVersion || ''};
  }

  async getAppInfo() {
    let appInfo: GetAppInfoResult;
    if (this.isHarmony) {
      appInfo = this.getAppInfoFromHarmony(navigator.userAgent);
    } else {
      appInfo = await bridge.getAppInfo().catch((error: any) => {
        console.log('getUA error', error);
      });
    }
    if (appInfo) {
      // 桥失败，则 appInfo 为空
      this.commonParams.nb_appversion = appInfo.version;
    }
  }

  async getDeviceInfo() {
    let deviceInfo;
    // 获取设置信息
    if (this.isHarmony) {
      deviceInfo = {};
    } else {
      deviceInfo = await bridge.getDeviceInfo().catch((error) => {
        // 这里若throw 会阻赛代码执行
        console.log('deviceInfo', error);
      });
    }
    if (deviceInfo) {
      // 可能为空
      // 这里的 value = 'empty' 需要默认在init里，不然桥失败时这里 deviceInfo 为 undefined； 会导致页面空白

      // 参数nb_device_model、nb_adid确认和ios保持一致
      // 参数nb_deviceid ios逻辑为uuid || utmContent
      const { model, imei, idfa } = deviceInfo;
      this.commonParams.nb_device_model = model || '';
      this.commonParams.nb_deviceid = imei || this.utmContent || '';
      this.commonParams.nb_adid = idfa || '';
    }
  }

  async getBiometricsInfo() {
    if (this.isHarmony) {
      try {
        const serviceInfo = await bridge.getServiceInfo();
        this.commonParams.nb_version = serviceInfo?.version || '';
      } catch (e) {
        console.log('getServiceInfo', e);
      }
    } else {
      // 获取生物识别信息
      const biometricsInfo = await bridge.getBiometricsInfo().catch((error) => {
        console.log('biometricsInfo', error);
        // 这里若throw 会阻赛代码执行
      });
      if (biometricsInfo) {
        // 可能为空
        // 获取风控信息
        // tslint:disable-next-line:max-line-length
        const { nb_fingerprint, nb_version, printType = '', finger_data = '', face_data = '', has_faceid = 0, has_touchid = 0, model_key = '', support_finger_type, hardwareDetected, support_faceid = 0, support_touchid = 0, faceid_type } = biometricsInfo as any;
        this.commonParams.nb_fingerprint = nb_fingerprint;
        this.commonParams.nb_version = nb_version;
        // pay.getBiometricsInfo：https://km.sankuai.com/page/191425618
        this.biometricsInfoParams = {
          printType,
          finger_data,
          face_data,
          has_faceid, // 只有ios生物桥会返回，为number 1 和0
          has_touchid: Number(has_touchid), // 安卓生物桥返回的类型为string "1" 和"0"，ios生物桥返回的类型为number 1 和0，现统一为number
          model_key,
          support_finger_type,
          hardwareDetected,
          support_faceid,
          support_touchid,
          faceid_type
        };
      }
    }
  }

  async getLocation() {
    // 通过knb设置经纬度
    const location = await bridge.getLocation().catch((error) => {
      console.log(error);
    });
    if (location) {
      const { lat, lng } = location;
      this.commonParams.nb_location = `${lat}_${lng}`;
    }
  }

  async getUserInfo() {
    // 传递给后台的nb_uuid必须为准确的设备号，但是后台会在cookie中重新设置nb_uuid，所以将从cookie中获取改为从knb中获取
    const user = await bridge.getUserInfo().catch((error) => {
      // 这里若throw 会阻赛代码执行
      console.log('getUserInfo', error);
    });
    if (user && user.uuid) {
      // 为空时，使用getParam('iuuid', 'uuid', 'dpid')获取到的值
      this.commonParams.nb_uuid = user.uuid;
    }
    if (user && user.userId) {
      this.commonParams.userid = user.userId;
    }
  }

  // 获取 set 化参数
  initParamsOfSet() {
    if (!bridge.isNative()) {
      return Promise.resolve();
    }
    let isSettled = false;
    return new Promise((resolve, reject) => {
      KNB.ready(() => {
        KNB.use('pay.getPayCommonParam', {
          success: (res: any) => {
            if (isSettled) {
              return;
            }
            isSettled = true;
            if (!res) {
              resolve('');
              return;
            }
            this.memberId = res.member_id;
            resolve('');
          },
          fail: (error: any) => {
            if (isSettled) {
              return;
            }
            isSettled = true;
            // env 不能调用 monitor，会造成循环引用报错
            Owl.addError({ name: 'set 化参数获取失败', msg: error.message }, { tags: error });
            reject();
          },
        });
      });
      // 超时处理
      setTimeout(() => {
        if (isSettled) {
          return;
        }
        isSettled = true;
        Owl.addError('获取set 化参数超时', {
          tags: {
            knbReady: typeof KNB.ready === 'function',
            memberId: env.memberId,
          },
        });
        reject();
      }, 1000);
    });
  }

  reportInitEnv() {
    Owl.addError('initEnv 数据', {
      tags: {
        ...this.commonParams,
      },
      level: 'info',
      category: 'custom_info'
    });
  }
}

export const env = new Env();
